// A ticket's AI generations, each tied to the customer message it answered and the reply that went out,
// so the details panel can show one generation at a time.

import { parseUtcDate } from '@/lib/utils';
import { traceStepsFromEvents } from '@/adapters/sentinelWorkspace.trace.adapter';
import type { SentinelAiEventLogWireDto, SentinelWorkspaceDetailWireDto, TraceStep, WorkspaceMessage } from './types';

export interface ActivityRun {
  id: number;
  /** The root trace every stage of this generation shares; the panel keeps only these event logs. */
  rootKey: string;
  customerMessageId: number | null;
  /** The reply this generation sent: Sentinel's own send (by the ticket's record or the text it sent), else an automation's after the customer message it answered; never a person's. */
  replyMessageId: number | null;
  steps: TraceStep[];
}

/** Candy stores message times without a zone; every stamp is read as UTC, as the bubbles read it. */
const time = (iso: string): number => parseUtcDate(iso).getTime();

function byCreation(left: SentinelAiEventLogWireDto, right: SentinelAiEventLogWireDto): number {
  return time(left.created_at) - time(right.created_at) || Number(left.id) - Number(right.id);
}

/** Letters and digits only: the helpdesk re-renders the markdown it was sent (bullets, bold, line breaks). */
const comparable = (text: string): string => text.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/** A blueprint stage and the response it led to share one root trace: one generation. */
export function rootKeyOf(event: SentinelAiEventLogWireDto): string {
  return event.root_trace_id ?? event.trace_id ?? String(event.id);
}

/** Every AI generation on the ticket, oldest first. */
export function activityRunsFromApi(detail: SentinelWorkspaceDetailWireDto, messages: WorkspaceMessage[]): ActivityRun[] {
  const events = detail.aiEventLogs.filter((event) => event.event_kind === 'request').sort(byCreation);
  const byRoot = new Map<string, SentinelAiEventLogWireDto[]>();
  for (const event of events) {
    const key = rootKeyOf(event);
    byRoot.set(key, [...(byRoot.get(key) ?? []), event]);
  }
  // Blueprint revisions are not generations, but their steps belong to the generation they revised.
  const stepEvents = [...detail.aiEventLogs].sort(byCreation);
  const generations = [...byRoot.entries()];
  const replies = messages.filter((message) => message.from === 'agent' && !message.isPrivate).sort((left, right) => left.id - right.id);
  // Matched by message id, not time: ids only grow, while a zoneless candy timestamp can shift by the server's offset.
  // A bigint, so the wire can carry it as a string.
  const answered = (event: SentinelAiEventLogWireDto): number | null =>
    event.latest_message_id == null ? null : Number(event.latest_message_id);
  // The reply Sentinel itself sent, once the helpdesk has synced it back; it belongs to the run that wrote it.
  const { provider_message_id: sentSourceId, current_ai_event_log_id: sentRunId } = detail.ticket;
  const sentReply = sentSourceId == null ? undefined : replies.find((message) => message.sourceId === sentSourceId);

  return generations.map(([rootKey, stages], index) => {
    // The stage that wrote the reply names the generation; a blueprint-only stage has no reply.
    const lead = [...stages].reverse().find((stage) => stage.response_message?.trim()) ?? stages.at(-1)!;
    const customerMessageId = answered(lead) ?? answered(stages[0]!);
    const later = generations.slice(index + 1).map(([, generation]) => answered(generation.at(-1)!));
    // Refines answer the same message; the reply belongs to the last of them.
    const laterSameMessage = later.some((id) => id === customerMessageId);
    const nextMessageId = later.find((id): id is number => id !== null && id !== customerMessageId);
    // A sent reply the helpdesk has not synced back yet is shown under the negated id of the run that wrote it.
    const pendingSent = replies.find((message) => stages.some((stage) => message.id === -Number(stage.id)));
    const wroteSentReply = sentRunId != null && stages.some((stage) => Number(stage.id) === Number(sentRunId));
    const answeredWindow =
      customerMessageId === null
        ? []
        : replies.filter((message) => message.id > customerMessageId && (nextMessageId === undefined || message.id < nextMessageId));
    const candidates = laterSameMessage ? [] : answeredWindow;
    // The ticket names only its latest send; an earlier one is found by the text this run sent, which the helpdesk
    // syncs back with its signature or the quoted mail appended. A draft edited before sending matches nothing.
    const dispatched = stages
      .map((stage) => stage.extra_details?.['sentinelDispatchedResponse'])
      .find((text): text is string => typeof text === 'string' && comparable(text) !== '');
    const dispatchedReply =
      dispatched === undefined ? undefined : answeredWindow.find((message) => comparable(message.markdown).startsWith(comparable(dispatched)));
    // Only a reply Sentinel or an automation is known to have sent: any other agent message after the customer's is a
    // person's (or Intercom's auto-responder), even while this run's draft sits unsent in the composer.
    const reply =
      pendingSent ??
      (wroteSentReply ? sentReply : undefined) ??
      dispatchedReply ??
      candidates.find((message) => message.isAutomated);

    return {
      id: Number(lead.id),
      rootKey,
      customerMessageId,
      replyMessageId: reply?.id ?? null,
      steps: stepEvents
        .filter((event) => rootKeyOf(event) === rootKey)
        .flatMap((event) =>
          traceStepsFromEvents(event.extra_details?.['sentinelEvents'], event.request_id ?? event.trace_id ?? String(event.id)),
        ),
    };
  });
}

/** The generation behind each sent reply, so its bubble can open that generation in the panel. */
export function runIdsByReply(runs: ActivityRun[]): Map<number, number> {
  const byReply = new Map<number, number>();
  for (const run of runs) if (run.replyMessageId !== null) byReply.set(run.replyMessageId, run.id);
  return byReply;
}
